import { useEffect, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
} from '@mui/material'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { useNavigate } from 'react-router'
import useServerAuth from 'src/customHooks/useServerAuth'
import { getSPDataWithoutParams, getSPDataWithParams } from 'src/supportPortal/services/services'
import { getDataMapFromCommonResponse } from 'src/mentor/helpers/utilityFunctions'
import ProfileDrawer from 'src/supportPortal/components/profile/ProfileDrawer'
import ScreenerProfileViewer from 'src/supportPortal/components/profile/ScreenerProfileViewer'
import ProfileViewer from 'src/mentor/components/MentorProfilePage/components/ProfileViewer'

/**
 * Self-contained profile drawer for a single practitioner — opened from the practitioner
 * detail page's "View Profile" action. Fetches the fuller profile record (and, for
 * screeners, the screener profile) on open and renders it through the same ProfileViewer
 * the listing's drawer uses. Edit is gated to the roles that can save changes.
 */
export default function PractitionerProfileDrawer({
  open,
  onClose,
  practitioner,
  variant = 'therapist',
}) {
  const navigate = useNavigate()
  const { allRolesInBoolean } = useServerAuth()
  const [loading, setLoading] = useState(false)
  const [fullProfileData, setFullProfileData] = useState(null)
  const [screenerProfileData, setScreenerProfileData] = useState(null)
  const [allTags, setAllTags] = useState(null)

  const isScreener = variant === 'screener'
  const practitionerId = practitioner?.id

  const canEditPractitionerProfile = Boolean(
    practitionerId &&
      (allRolesInBoolean.isAdmin ||
        (variant === 'doctor'
          ? allRolesInBoolean.isClinicSuccessAdmin
          : allRolesInBoolean.isTherapistAdmin)),
  )

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!open || !practitionerId) return
      setLoading(true)
      setFullProfileData(null)
      setScreenerProfileData(null)
      setAllTags(null)
      try {
        const profileResp = await getSPDataWithoutParams(
          `/api/secure/cc/support/practitioner/profile?practitioner-id=${practitionerId}`,
        )
        if (!cancelled && profileResp?.status === 200) {
          const profileData =
            profileResp.data?.payload || profileResp.data?.data || profileResp.data || null
          if (profileData) setFullProfileData(profileData)
        }

        const tagsResp = await getSPDataWithoutParams('/api/secure/cc/support/domain/all/list')
        if (!cancelled && tagsResp?.status === 200) {
          const tagsData =
            tagsResp.data?.payload || tagsResp.data?.data || tagsResp.data || null
          if (tagsData) {
            setAllTags(Array.isArray(tagsData) ? tagsData : tagsData?.domains || [])
          }
        }

        if (isScreener) {
          const screenerResp = await getSPDataWithParams(
            '/api/secure/cc/support/screeners/profiles/by-mentor-id',
            { mentorId: practitionerId, includeDisabled: true, includeDeleted: false },
          )
          if (!cancelled && screenerResp?.status === 200) {
            const raw =
              getDataMapFromCommonResponse(screenerResp) ||
              screenerResp?.data?.data ||
              screenerResp?.data
            const data = raw?.screenerProfile ?? raw?.profile ?? raw
            setScreenerProfileData(data && typeof data === 'object' ? data : null)
          }
        }
      } catch (error) {
        console.error('Error fetching practitioner profile:', error)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [open, practitionerId, isScreener])

  function handleEdit() {
    if (!practitionerId) return
    onClose?.()
    navigate(
      `/support-home/practitioner-management/onboarding?type=${encodeURIComponent(
        variant,
      )}&practitioner-id=${encodeURIComponent(practitionerId)}`,
    )
  }

  const profileViewer = (
    <ProfileViewer
      profileData={fullProfileData || practitioner}
      allTags={allTags}
      onEdit={canEditPractitionerProfile ? handleEdit : null}
      readApiWithoutParams={getSPDataWithoutParams}
      techniqueApiPathBuilder={(techniqueId) => `/api/secure/cc/support/technique/${techniqueId}`}
      certificateListApiPathBuilder={(profileData) =>
        `/api/secure/cc/support/practitioner/certificate-record/list/paginated?practitioner-id=${profileData?.id}&page=0&size=100`
      }
    />
  )

  return (
    <ProfileDrawer
      open={open}
      onClose={onClose}
      title={
        practitioner
          ? `${practitioner?.name || practitioner?.firstName || 'Practitioner'}${
              isScreener ? ' (Screener)' : ''
            } – Profile`
          : 'Profile'
      }
    >
      {loading && !fullProfileData && !screenerProfileData ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
          <CircularProgress />
        </Box>
      ) : isScreener ? (
        <Box sx={{ pt: 0 }}>
          <Accordion
            defaultExpanded
            disableGutters
            sx={{
              boxShadow: 'none',
              '&:before': { display: 'none' },
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ fontWeight: 600 }}>
              Screener Profile
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <ScreenerProfileViewer profile={screenerProfileData} />
            </AccordionDetails>
          </Accordion>
          <Accordion
            disableGutters
            sx={{
              boxShadow: 'none',
              '&:before': { display: 'none' },
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ fontWeight: 600 }}>
              Mentor / Practitioner Profile
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>{profileViewer}</AccordionDetails>
          </Accordion>
        </Box>
      ) : (
        profileViewer
      )}
    </ProfileDrawer>
  )
}
